import { USER_TYPE, YARN_FORM_TYPE, PLAN_ID, USER_POSITION, COUNT_URL, BAIDU, baiduUrl, connectionUrl } from '/constants/common.js'
import isObject from 'lodash/isObject'
import ChartBarSolid  from '~/components/svgs/ChartBarSolid.vue'
import QuestionCircleSolid  from '~/components/svgs/QuestionCircleSolid.vue'

export function usePublicCommon() {
  if (process.client) {
    const authStore = useAuthStore();
    authStore.setRefreshToken(true);
  }
}

export function useRecordEnterUrl() {
  const { handleChangeLang } = useLocaleCommon();
  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const baseStore = useBaseStore();
  const authStore = useAuthStore();
  const localePath = useLocalePath();
  const componentStore = useComponentStore()

  const prevRoute = ref(null);
  watchEffect(() => {
    prevRoute.value = route.fullPath;
  });
  const recordNavInfo = () => {
    baseStore.$patch({
      urlNav: {
        name: route.name.split("__")[0],
        params: {
          id: route.params.id,
        },
      }
    })
  };
  recordNavInfo();
  const navigateAndShowError = (targetRouteName, lang, errorCode) => {
    router.push(localePath(targetRouteName, lang));
    componentStore.$patch({ errorMessage: t(`error.${errorCode}`), hasError: true });
  };

  const reloadWithOwnLang = async () => {
    if (authStore.user.basicLanguage !== route.name.split("___")[1]) {
      await handleChangeLang(authStore.user.basicLanguage);
    }
  };

  return { prevRoute, recordNavInfo, navigateAndShowError, reloadWithOwnLang };
}

export const useUserOnlyPage = () => {
  const userStore = useUserStore();
  const banToEnterPage = ref(false);

  const checkAuth = () => {
    if (![USER_TYPE.GUEST, USER_TYPE.DEMO].includes(userStore.userType)) {
      banToEnterPage.value = true;
      showError({ statusCode: 403 });
    }
  };
  return { banToEnterPage, checkAuth };
};

export const useSupplierOnlyPage = () => {
  const userStore = useUserStore();
  const banToEnterPage = ref(false);

  const checkAuth = () => {
    if (
      ![USER_TYPE.SUPPLIER, USER_TYPE.DEMO].includes(userStore.userType)
    ) {
      banToEnterPage.value = true;
      showError({ statusCode: 403 });
    }
  };
  return { banToEnterPage, checkAuth };
};

export const useBaidu = () => {
  const baiduHead = () => {
    return {
      head() {
        return {
          script: [BAIDU]
        }
      }
    }
  }
  return { baiduHead }
}

export const useBaiduStatistics = () => {
  const authStore = useAuthStore()
  const route = useRoute()
  const baseStore = useBaseStore()

  const handleBaidu = () => {
    const _bd = (window._bd = window._bd || [])
    if (
      !baseStore.adminMode &&
      route.name.includes('index___')
    ) {
      _bd.push(['_trackPageview', route.path])
    } else if (
      authStore.loggedIn &&
      authStore.user?.userType === USER_TYPE.GUEST
    ) {
      _bd.push(['_trackPageview', route.path])
    } else {
      _bd.push(['doNotTrack'])
    }
  }

  const checkDomain = () => {
    const serverEnv = baseStore.serverEnv
    const origin = window.location.origin

    if (serverEnv === 'ss-production') {
      for (let i = 1; i <= COUNT_URL; i++) {
        if (origin === connectionUrl[2][i]) {
          BAIDU.src = baiduUrl[2][i]
        }
      }
    } else if (serverEnv === 'ss-staging') {
      for (let i = 1; i <= COUNT_URL; i++) {
        if (origin === connectionUrl[1][i]) {
          BAIDU.src = baiduUrl[1][i]
        }
      }
    } else {
      for (let i = 1; i <= COUNT_URL; i++) {
        if (origin === connectionUrl[0][i]) {
          BAIDU.src = baiduUrl[0][i]
        }
      }
    }
  }

  const refreshAuth = async () => {
    try {
      const userData = await baseStore.getUserMe()
      userData.userType = await baseStore.getUserType(userData.userType)

      if (
        userData &&
        userData.userType !== USER_TYPE.DEMO &&
        userData.countryCode === 'CN'
      ) {
        checkDomain()
        handleBaidu()
      }
    } catch (error) {
      console.error('Error refreshing auth:', error)
    }
  }

  const initBaiduStatistics = () => {
    if (
      !authStore.user &&
      !route.name.includes('index___') &&
      !route.name.includes('company-create___')
    ) {
      refreshAuth()
    }

    if (
      authStore.user &&
      authStore.user.userType !== USER_TYPE.DEMO &&
      authStore.user.countryCode === 'CN'
    ) {
      checkDomain()
      handleBaidu()
    }
  }

  onMounted(() => {
    initBaiduStatistics()
  })

  return {
    initBaiduStatistics,
    handleBaidu,
    checkDomain,
    refreshAuth
  }
}

export const useYarnDetail = () => {
  const yarnStore = useYarnStore()
  const componentStore = useComponentStore()
  const statistictStore = useStatisticStore()
  const route = useRoute()

  const yarnInfoMode = computed(() => componentStore.yarnInfoMode)

  const getYarnlogId = async () => {
    let yarnlogId = yarnStore.yarnlogId || route.params.id
    yarnlogId = statistictStore.waitingFromApex
      ? statistictStore.waitingFromApex
      : yarnlogId
    yarnlogId =
      isObject(yarnlogId) && yarnlogId.yarnlogId
      ? yarnlogId.yarnlogId
      : yarnlogId
    return Number(yarnlogId)
  }

  return {
    yarnInfoMode,
    getYarnlogId
  }
}

export const useSearchFormCommon = (locale, initialValue) => {
  const searchStore = useSearchStore()
  const storageStore = useStorageStore()
  const authStore = useAuthStore()
  const router = useRouter()
  const route = useRoute()
  const localePath = useLocalePath()
  const form = ref(initialValue || getSearchInitValue())

  const showSearchSideBar = computed(() => searchStore.showSearchSideBar)
  const isMyYarnPage = computed(() => searchStore.isMyYarnPage)
  const isColorSearch = computed(() => searchStore.isColorSearch)

  const toggleSearchSide = (value) => {
    storageStore.$patch({ showSearchSideBar: value })
  }

  const checkRoute = (colored) => {
    if (colored) {
      return isMyYarnPage.value
        ? { name: `myyarns___${locale}`, query: { page: 1 } }
        : { name: `yarns-color___${locale}`, query: { page: 1 } }
    } else {
      return isMyYarnPage.value
        ? { name: `myyarns___${locale}`, query: { page: 1 } }
        : { name: `yarns___${locale}`, query: { page: 1 } }
    }
  }

  const resetCertification = () => {
    form.value.selectedCertifications = []
  }

  const resetMaterial = () => {
    form.value.selectedMaterial = []
  }

  const handleAutocompleteHistory = (search) => {
    if (search.keyword) {
      storageStore.addKeywordSearchResult(search.keyword)
    }
  }

  const searchSubmit = () => {
    if (showSearchSideBar.value === true) toggleSearchSide(false)
    const search = provideMappedSearch(form.value)
    const companyId = isMyYarnPage.value ? authStore.user?.companyId : ''
    handleAutocompleteHistory(search)
    storageStore.$patch({ searchCondition: JSON.parse(JSON.stringify(form.value)) })
    const baseName = route.name?.toString().split('___')[0]
    if (search.colored) {
      searchStore.$patch({ isColorSearch: true })
      if (['yarns-color', 'myyarns'].includes(baseName)) {
        storageStore.getSearchColorList({
          page: 1,
          isMyYarnPage: isMyYarnPage.value,
          companyId
        })
      } else {
        router.push(localePath(checkRoute(true)))
      }
    } else {
      searchStore.$patch({ isColorSearch: false })
      if (['yarns', 'myyarns'].includes(baseName)) {
        storageStore.getSearchList({
          page: 1,
          isMyYarnPage: isMyYarnPage.value,
          companyId
        })
      } else {
        router.push(localePath(checkRoute(false)))
      }
    }
  }

  const resetSubmit = () => {
    const initValue = getSearchInitValue()
    storageStore.$patch({ searchCondition: initValue })
    form.value = getSearchInitValue()
  }
  

  return {
    showSearchSideBar,
    isColorSearch,
    form,
    toggleSearchSide,
    checkRoute,
    resetCertification,
    resetMaterial,
    searchSubmit,
    getSearchInitValue,
    resetSubmit
  }
}

export const useDisableAutoComplete = () => {
  const disableAutoComplete = () => {
    const elements = document.querySelectorAll('[autocomplete="off"]')

    if (!elements) {
      return
    }
    try {
      elements.forEach(element => {
        element.setAttribute('readonly', 'readonly')
        element.style.backgroundColor = 'inherit'

        setTimeout(() => {
          element.removeAttribute('readonly')
          element.removeAttribute('style')
        }, 500)
      })
    } catch (e) {
      console.warn('ignore error', e)
    }
  }

  onMounted(() => {
    disableAutoComplete()
  })
  return { disableAutoComplete }
}

export const useHelpTopControl = () => {
  const showRightForm = ref(false)
  const authStore = useAuthStore()

  const setShowRightForm = () => {
    showRightForm.value = authStore.user.userType === USER_TYPE.DEMO
  }

  return { showRightForm, setShowRightForm }
}

export const useStorageChecker = () => {
  const companyStore = useCompanyStore()
  const { $notify } = useNuxtApp()
  const { t } = useI18n()

  const checkStorage = async (companyId, onSuccess, onError) => {
    handleApiResult(
      companyStore.getStorageCapacity({ companyId }),
      ({ maxsize, usecapacity }) => {
        if (maxsize <= usecapacity) {
          $notify(setNotificationConfig({ type: 'error', text: t('error.10713') }));
          onError && onError()
        } else {
          onSuccess && onSuccess()
        }
      },
      { store: useBaseStore() }
    )
  }

  return { checkStorage }
}

export const useMaterialForm = () => {
  const searchStore = useSearchStore()

  const getMaterialName = async (locale, materials, localeOrder) => {
    await searchStore.setMaterials(1)
    const yarnMaterial = await searchStore.getAllMaterial(locale)
    return materials
      .map(({ materialId, materialOption, materialDetailOption, composition, i18nMaterials }) => {
        let displayMaterialName = ''
        let materialName = ''
        if (
          materialId &&
          yarnMaterial.find(({ codeId }) => {
            return materialId === codeId
          })
        ) {
          materialName =
            yarnMaterial.find(({ codeId }) => {
              return materialId === codeId
            }).codeName + ' '
        }

        let materialOptionName = ''
        if (
          materialOption &&
          yarnMaterial.find(({ codeId }) => {
            return materialOption === codeId
          })
        ) {
          materialOptionName =
            yarnMaterial.find(({ codeId }) => {
              return materialOption === codeId
            }).codeName + ' '
        }

        let materialDetailOptionName = ''
        if (
          materialDetailOption &&
          yarnMaterial.find(({ codeId }) => {
            return materialDetailOption === codeId
          })
        ) {
          materialDetailOptionName =
            yarnMaterial.find(({ codeId }) => {
              return materialDetailOption === codeId
            }).codeName + ' '
        }

        if (materialDetailOption) {
          displayMaterialName = materialDetailOptionName
        } else {
          if (materialOption) {
            displayMaterialName = materialOptionName
          } else {
            if (materialId) {
              displayMaterialName = materialName
            }
          }
        }
        return (
          (localeOrder?.reduce((previous, current) => {
            const target = i18nMaterials.i18ns.find(({ locale }) => {
              return locale === current
            })
            if (previous) {
              return previous
            }
            if (target?.displayName) {
              return target?.displayName
            }
            return ''
          }, '') ||
            displayMaterialName) +
          ' ' +
          composition +
          '% '
        )
      })
      .join(', ')
  }

  return {
    getMaterialName,
  }
}
export const useYarnPreviewPage = () => {
  const yarnStore = useYarnStore()
  const baseStore = useBaseStore()
  const searchStore = useSearchStore()
  const componentStore = useComponentStore()
  const router = useRouter()
  const route = useRoute()
  const localePath = useLocalePath()
  const { t, locale } = useI18n()

  const isAdmin = computed(() => baseStore.adminMode)

  const isCreatePreview = computed(() => {
    if (isAdmin.value) {
      return route.params.yarnlogId === 'new'
    }
    return route.params.id === 'new'
  })

  const confirmText = computed(() =>
    isCreatePreview.value ? t('common.10287') : t('myyarns/id/preview.10122')
  )

  const backToText = computed(() => {
    if (
      [YARN_FORM_TYPE.CUSTOMER_CREATE, YARN_FORM_TYPE.ADMIN_CREATE].includes(
        componentStore.yarnFormType
      )
    ) {
      return t('myyarns/id/preview.10580')
    }
    return t('myyarns/id/preview.10121')
  })

  const colorTypeMapper = computed(() => {
    const options = yarnStore.colorTypeOptions
    return options && options.length
      ? options.reduce((previous, current) => {
          previous[current.codeId] = current.apexId
          return previous
        }, {})
      : null
  })

  const colorMapper = computed(() => {
    const colors = searchStore.colors
    return colors && colors.length
      ? colors.reduce((previous, current) => {
          previous[current.codeId] = current.apexId
          return previous
        }, {})
      : null
  })

  const navigate = (routeInfo, successCallback) => {
    router.push(localePath(routeInfo), successCallback)
  }

  const upload = () => {
    if (isCreatePreview.value) {
      handleApiResult(
        yarnStore.createLitePlanBook({
          skipNotification: true,
          yarnNo: 1,
          yarnId: `{${getUppercaseUuidv4(window.crypto)}}`,
          ...yarnStore.yarnForm
        }),
        () => {
          navigate(
            isAdmin.value
              ? {
                  name: 'admins-supplier-id',
                  params: { id: route.params.id }
                }
              : { name: 'myyarns', query: { page: 1 } }
          )
        },
        { t, store: baseStore }
      )
    } else {
      handleApiResult(
        yarnStore.updateWholeYarn({
          skipNotification: true,
          locale: locale.value,
          ...yarnStore.yarnForm,
          colors: yarnStore.yarnFormColors,
          apexColorCategory: colorMapper.value,
          apexColorType: colorTypeMapper.value
        }),
        () => {
          navigate(
            isAdmin.value
              ? {
                  name: 'admins-supplier-id',
                  query: { page: 1 },
                  params: {
                    id: route.params.id
                  }
                }
              : { name: 'myyarns', query: { page: 1 } }
          )
        },
        { t, store: baseStore }
      )
    }
  }

  const backToEdit = () => {
    if (isAdmin.value) {
      if (route.params.yarnlogId === 'new') {
        return navigate({
          name: 'admins-supplier-id-create',
          params: {
            id: route.params.id
          }
        })
      } else {
        return navigate({
          name: 'admins-supplier-id-yarnlogId-edit',
          params: {
            id: route.params.id,
            yarnlogId: route.params.yarnlogId
          }
        })
      }
    } else if (
      componentStore.yarnFormType === YARN_FORM_TYPE.CUSTOMER_CREATE
    ) {
      return navigate({
        name: 'myyarns-create'
      })
    }
    navigate({
      name: 'myyarns-id-edit',
      params: {
        id: route.params.id
      }
    })
  }

  onMounted(() => {
    if (yarnStore.yarnForm === null) {
      // prevent page reload and url navigation
      // store.dispatch(
      //   'notifications/show',
      //   setNotificationConfig({
      //     type: 'warn',
      //     text: t('common.10397')
      //   })
      // )
      backToEdit()
    }
  })

  return {
    isCreatePreview,
    confirmText,
    backToText,
    colorTypeMapper,
    colorMapper,
    upload,
    backToEdit
  }
}

export const useReportCommon = () => {
  const { t } = useI18n()
  const authStore = useAuthStore()
  const router = useRouter()
  const localePath = useLocalePath()
  const navInfos = ref([
    {
      path: {
        name: 'report-book',
        query: {
          page: 1
        }
      },
      icon: ChartBarSolid,
      label: t('myyarns.10114')
    },
    {
      path: {
        name: 'report-category'
      },
      icon: ChartBarSolid,
      label: t('myyarns.10115')
    },
    {
      path: {
        name: 'report-markettrend'
      },
      icon: ChartBarSolid,
      label: t('myyarns.10116'),
    }
  ])

  const isLitePlanUser = () => {
    return PLAN_ID.LITE.value === authStore.user.planId && USER_TYPE.SUPPLIER === authStore.user.userType
  }

  const donForLitePlan = () => {
    if (isLitePlanUser()) {
      navInfos.value[2].disabled = true
    }
  }

  const goBackToStaffFirstPage = () => {
    router.push(localePath({
      name: 'myyarns',
      query: { page: 1 }
    }))
  }

  const checkSupplierPlan = () => {
    if (isLitePlanUser()) {
      goBackToStaffFirstPage()
    }
  }

  const checkLogFlag = () => {
    const user = authStore.user
    if (user.userType !== USER_TYPE.DEMO &&
      (user.userType !== USER_TYPE.SUPPLIER ||
      (user.logFlag === 'N ' && user.userRole === USER_POSITION.STAFF))) {
        goBackToStaffFirstPage()
    }
  }

  onMounted(() => {
    checkLogFlag()
    donForLitePlan()
  })

  return {
    navInfos,
    isLitePlanUser,
    donForLitePlan,
    goBackToStaffFirstPage,
    checkSupplierPlan,
    checkLogFlag
  }
}

export function useAdminProfile() {
  const { t } = useI18n()

  const navInfos = ref([
    {
      path: {
        name: 'admins-profile'
      },
      label: t('user/profile.10176')
    },
    {
      path: {
        name: 'admins-profile-help'
      },
      icon: QuestionCircleSolid,
      label: t('user/profile.10178')
    },
    {
      path: {
        name: 'admins-profile-terms'
      },
      label: t('user/profile.10179')
    }
  ])
  return { navInfos }
}
